import "./App.css";
import "shepherd.js/dist/css/shepherd.css";

import { Routes, Route, Outlet } from "react-router-dom";
import { withSentryReactRouterV6Routing } from "@sentry/react";
import { ReactElement, useEffect, useState, lazy, Suspense } from "react";
import Unavailable4MobileError from "./components/errors/Unavailable4MobileError";
import MainLoading from "./components/MainLoading";
import ResellerRequiredWrapper from "./components/wrappers/ResellerRequiredWrapper";

const ProposalsPage = lazy(() => import("./pages/proposals"));
const ProposalDetailPage = lazy(() => import("./pages/proposals/detail"));
const FgtsSimulationPage = lazy(() => import("./pages/contratar/fgts/index.tsx"));
const CltSimulationPage = lazy(() => import("./pages/contratar/clt/index.tsx"));
const LoginPage = lazy(() => import("./pages/usuarios/login"));
const LogoutPage = lazy(() => import("./pages/usuarios/logout"));
const ResellersNUsersPage = lazy(
  () => import("./pages/lojas-e-usuarios/index.tsx")
);
const ResellersPage = lazy(
  () => import("./pages/lojas-e-usuarios/lojas/index.tsx")
);
const UsersPage = lazy(
  () => import("./pages/lojas-e-usuarios/usuarios/index.tsx")
);
const EditUserPage = lazy(
  () => import("./pages/lojas-e-usuarios/usuarios/[userId]/editar.tsx")
);
const CreateUserPage = lazy(
  () =>
    import("./pages/lojas-e-usuarios/usuarios/[resellerId]/usuarios/criar.tsx")
);
const EditResellerPage = lazy(
  () => import("./pages/lojas-e-usuarios/resellers/[resellerId]/atualizar.tsx")
);
const CreateResellerPage = lazy(
  () => import("./pages/lojas-e-usuarios/resellers/criar.tsx")
);
const MainLayout = lazy(() => import("./pages/MainLayout"));
const CommissionsPage = lazy(() => import("./pages/comissoes/index.tsx"));
const IntegrationsPage = lazy(() => import("./pages/integracoes/index.tsx"));
const DashboardPage = lazy(() => import("./pages/dashboards"));
const LeadTrackingPage = lazy(() => import("./pages/rastreamento-de-lead"));
const UICustomizationPage = lazy(() => import("./pages/estilo/index.tsx"));
const NotificationsPage = lazy(() => import("./pages/notificacoes/index.tsx"));
const ProductsConfigurationLayout = lazy(
  () => import("@/components/productsConfiguration/ProductsConfigurationLayout")
);
const ProductsConfigurationPage = lazy(
  () => import("@/pages/tabelas/index.tsx")
);
const ProductsConfigurationForRolePage = lazy(
  () => import("@/pages/tabelas/[roleId]/index.tsx")
);

// Proposal Metadata
const ProposalMetadataPage = lazy(() => import("./pages/proposal-metadata"));
const ProposalMetadataTypeCreateForm = lazy(
  () => import("./pages/proposal-metadata/create-form.tsx")
);
const ProposalMetadataTypeEditForm = lazy(
  () => import("./pages/proposal-metadata/edit-form.tsx")
);

// Commercial Information
const AnalyticalInformationPage = lazy(
  () => import("./pages/informacoes-analiticas")
);
const CreateCommercialManagerPage = lazy(
  () => import("./pages/informacoes-analiticas/gerentes-comerciais/criar")
);
const EditCommercialManagerPage = lazy(
  () =>
    import(
      "./pages/informacoes-analiticas/gerentes-comerciais/[commercialManagerId]/editar"
    )
);
const CreateRegionalManagerPage = lazy(
  () => import("./pages/informacoes-analiticas/gerentes-regionais/criar")
);
const EditRegionalManagerPage = lazy(
  () =>
    import(
      "./pages/informacoes-analiticas/gerentes-regionais/[regionalManagerId]/editar"
    )
);
const CreateSuperintendentPage = lazy(
  () => import("./pages/informacoes-analiticas/superintendentes/criar")
);
const EditSuperintendentPage = lazy(
  () =>
    import(
      "./pages/informacoes-analiticas/superintendentes/[superintendentId]/editar"
    )
);

//Cleansing Batch
const CleansingBatch = lazy(
  () => import("./pages/higienizacao-de-cpfs/index.tsx")
);
const CleansingBatchCreateForm = lazy(
  () => import("./pages/higienizacao-de-cpfs/novo.tsx")
);

// Not Found
const NotFound = lazy(() => import("./pages/NotFound.tsx"));

const SentryRoutes = withSentryReactRouterV6Routing(Routes);

function SuspenseBoundary() {
  return (
    <Suspense fallback={<MainLoading />}>
      <Outlet />
    </Suspense>
  );
}

export default function App() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(window.innerWidth < 768);
  }, []);

  return (
    <SentryRoutes>
      <Route path="/" element={<SuspenseBoundary />}>
        <Route path="/usuarios/login" element={<LoginPage />} />
        <Route path="/usuarios/logout" element={<LogoutPage />} />{" "}
        <Route path="/" element={<MainLayout />}>
          <Route
            path="/contratar/fgts/*"
            element={
              <ResellerRequiredWrapper element={<FgtsSimulationPage />} />
            }
          />
          <Route
            path="/contratar/clt/*"
            element={
              <ResellerRequiredWrapper element={<CltSimulationPage />} />
            }
          />
          <Route
            path="/lojas-e-usuarios"
            element={
              <Unavailable4MobileErrorWrapper
                isMobile={isMobile}
                element={<ResellersNUsersPage />}
              />
            }
          />
          <Route
            path="/lojas-e-usuarios/lojas"
            element={
              <Unavailable4MobileErrorWrapper
                isMobile={isMobile}
                element={<ResellersPage />}
              />
            }
          />
          <Route
            path="/lojas-e-usuarios/usuarios"
            element={
              <Unavailable4MobileErrorWrapper
                isMobile={isMobile}
                element={<UsersPage />}
              />
            }
          />
          <Route
            path="/lojas-e-usuarios/usuarios/:userId/atualizar"
            element={
              <Unavailable4MobileErrorWrapper
                isMobile={isMobile}
                element={<EditUserPage />}
              />
            }
          />
          <Route
            path="/lojas-e-usuarios/:resellerId/usuarios/criar"
            element={
              <Unavailable4MobileErrorWrapper
                isMobile={isMobile}
                element={<CreateUserPage />}
              />
            }
          />
          <Route
            path="/lojas-e-usuarios/lojas/criar"
            element={
              <Unavailable4MobileErrorWrapper
                isMobile={isMobile}
                element={<CreateResellerPage />}
              />
            }
          />
          <Route
            path="/lojas-e-usuarios/lojas/:resellerId/atualizar"
            element={
              <Unavailable4MobileErrorWrapper
                isMobile={isMobile}
                element={<EditResellerPage />}
              />
            }
          />
          <Route path="/propostas?" element={<ProposalsPage />} />
          <Route
            path="/propostas/:proposalId"
            element={<ProposalDetailPage />}
          />
          <Route
            path="/comissoes"
            element={
              <Unavailable4MobileErrorWrapper
                isMobile={isMobile}
                element={<CommissionsPage />}
              />
            }
          />
          <Route
            path="/integracoes"
            element={
              <ResellerRequiredWrapper
                element={
                  <Unavailable4MobileErrorWrapper
                    isMobile={isMobile}
                    element={<IntegrationsPage />}
                  />
                }
              />
            }
          />
          <Route
            path="/dashboards"
            element={
              <Unavailable4MobileErrorWrapper
                isMobile={isMobile}
                element={<DashboardPage />}
              />
            }
          />
          <Route
            path="/rastreamento-de-lead"
            element={
              <Unavailable4MobileErrorWrapper
                isMobile={isMobile}
                element={<LeadTrackingPage />}
              />
            }
          />
          <Route
            path="/estilo"
            element={
              <ResellerRequiredWrapper
                element={
                  <Unavailable4MobileErrorWrapper
                    isMobile={isMobile}
                    element={<UICustomizationPage />}
                  />
                }
              />
            }
          />
          <Route
            path="/notificacoes"
            element={
              <Unavailable4MobileErrorWrapper
                isMobile={isMobile}
                element={<NotificationsPage />}
              />
            }
          />
          <Route path="/tabelas" element={<ProductsConfigurationLayout />}>
            <Route
              path="/tabelas"
              element={
                <Unavailable4MobileErrorWrapper
                  isMobile={isMobile}
                  element={<ProductsConfigurationPage />}
                />
              }
            />
            <Route
              path="/tabelas/:roleId"
              element={
                <Unavailable4MobileErrorWrapper
                  isMobile={isMobile}
                  element={<ProductsConfigurationForRolePage />}
                />
              }
            />
          </Route>
          <Route path="/higienizacao-de-cpfs" element={<CleansingBatch />} />
          <Route
            path="/higienizacao-de-cpfs/novo"
            element={<CleansingBatchCreateForm />}
          />
          <Route
            path="/metadados-proposta"
            element={
              <ResellerRequiredWrapper
                element={
                  <Unavailable4MobileErrorWrapper
                    isMobile={isMobile}
                    element={<ProposalMetadataPage />}
                  />
                }
              />
            }
          />
          <Route
            path="/metadados-proposta/campanha-marketing/novo"
            element={
              <ResellerRequiredWrapper
                element={
                  <Unavailable4MobileErrorWrapper
                    isMobile={isMobile}
                    element={
                      <ProposalMetadataTypeCreateForm metaT="marketing-campaign" />
                    }
                  />
                }
              />
            }
          />
          <Route
            path="/metadados-proposta/campanha-marketing/:typeId/editar"
            element={
              <ResellerRequiredWrapper
                element={
                  <Unavailable4MobileErrorWrapper
                    isMobile={isMobile}
                    element={
                      <ProposalMetadataTypeEditForm metaT="marketing-campaign" />
                    }
                  />
                }
              />
            }
          />
          <Route
            path="/metadados-proposta/canal-vendas/novo"
            element={
              <ResellerRequiredWrapper
                element={
                  <Unavailable4MobileErrorWrapper
                    isMobile={isMobile}
                    element={
                      <ProposalMetadataTypeCreateForm metaT="selling-channel" />
                    }
                  />
                }
              />
            }
          />
          <Route
            path="/metadados-proposta/canal-vendas/:typeId/editar"
            element={
              <ResellerRequiredWrapper
                element={
                  <Unavailable4MobileErrorWrapper
                    isMobile={isMobile}
                    element={
                      <ProposalMetadataTypeEditForm metaT="selling-channel" />
                    }
                  />
                }
              />
            }
          />
          <Route
            path="/informacoes-analiticas"
            element={
              <Unavailable4MobileErrorWrapper
                isMobile={isMobile}
                element={<AnalyticalInformationPage />}
              />
            }
          />
          <Route
            path="/informacoes-analiticas/gerentes-comerciais/criar"
            element={
              <Unavailable4MobileErrorWrapper
                isMobile={isMobile}
                element={<CreateCommercialManagerPage />}
              />
            }
          />
          <Route
            path="/informacoes-analiticas/gerentes-comerciais/:commercialManagerId/editar"
            element={
              <Unavailable4MobileErrorWrapper
                isMobile={isMobile}
                element={<EditCommercialManagerPage />}
              />
            }
          />
          <Route
            path="/informacoes-analiticas/gerentes-regionais/criar"
            element={
              <Unavailable4MobileErrorWrapper
                isMobile={isMobile}
                element={<CreateRegionalManagerPage />}
              />
            }
          />
          <Route
            path="/informacoes-analiticas/gerentes-regionais/:regionalManagerId/editar"
            element={
              <Unavailable4MobileErrorWrapper
                isMobile={isMobile}
                element={<EditRegionalManagerPage />}
              />
            }
          />
          <Route
            path="/informacoes-analiticas/superintendentes/criar"
            element={
              <Unavailable4MobileErrorWrapper
                isMobile={isMobile}
                element={<CreateSuperintendentPage />}
              />
            }
          />
          <Route
            path="/informacoes-analiticas/superintendentes/:superintendentId/editar"
            element={
              <Unavailable4MobileErrorWrapper
                isMobile={isMobile}
                element={<EditSuperintendentPage />}
              />
            }
          />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Route>
    </SentryRoutes>
  );
}

function Unavailable4MobileErrorWrapper({
  isMobile,
  element,
}: {
  isMobile: boolean;
  element: ReactElement;
}) {
  if (isMobile) {
    return <Unavailable4MobileError originalElement={element} />;
  }
  return element;
}
